// 1. Framework / library imports
import { memo, useCallback, useEffect, useState } from "react";
import { ReactSVG } from "react-svg";

// 2. Helpers
import { classNames, useTranslate } from "helpers";

// 3. Validation & Types
import {
    FleetDocumentRow,
    FleetDocumentType,
    FleetDocumentUpload,
} from "services/api/fleet/vehicles/documents/types";

// 4. API & Data
import {
    createFleetDocumentAPI,
    deleteFleetDocumentUploadAPI,
    getFleetDocumentsAPI,
    getFleetDocumentTypesAPI,
} from "services/api/fleet/vehicles/documents/api";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// 5. UI Components
import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";

// 7. Local Types
import { TireSetDocumentFormProps } from "./types";
import HorizontalScroll from "ui/components/Scroll/HorizontalScroll/HorizontalScroll";
import { Dialog } from "ui/components/Dialog";
import { PictureIcon } from "ui/components/Pictures/PictureIcon";


type SelectedFileType = {
    documentId: string;
    uploadId: string;
}

const initialSelectedFile = {
    documentId: "",
    uploadId: ""
}

const TireSetDocumentForm: React.FC<TireSetDocumentFormProps> = (props) => {
    const { formValues, setFormValues } = props;

    const { t } = useTranslate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState<SelectedFileType>(initialSelectedFile);
    const [selectedDocumentTypeId, setSelectedDocumentType] = useState<string>();
    const [documentTypes, setDocumentTypes] = useState<
        FleetDocumentType[]
    >([]);

    const [vehicleDocuments, setTireSetDocuments] = useState<
        DataWithPagination<FleetDocumentRow>
    >(initialDataWithPagination);

    useEffect(() => {
        fetchDocumentTypes();
        if (formValues.id)
            fetchTireSetDocuments(vehicleDocuments.pagination, formValues.id);
    }, [formValues.id]);

    const fetchTireSetDocuments = async (
        pagination: Pagination,
        tireSetId: string,
        name?: string
    ) => {
        setIsLoading(true);
        try {
            const results = await getFleetDocumentsAPI(
                pagination,
                name,
                tireSetId,
                "TireSet"
            );
            setTireSetDocuments(results);
        } catch {
            setTireSetDocuments(initialDataWithPagination);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDocumentTypes = async () => {
        setIsLoading(true);
        try {
            const results = await getFleetDocumentTypesAPI("", true);

            setDocumentTypes(results);
        } catch {
            setDocumentTypes([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    };

    const handleUploadSave = useCallback(
        async (uploads: FleetDocumentUpload[]) => {
            if (!formValues.id || uploads.length === 0) return;

            setIsLoading(true);
            try {
                const documentTypeId = selectedDocumentTypeId || documentTypes[0]?.id || null;
                const formDocument = {
                    name: null,
                    description: null,
                    documentTypeId,
                    files: [],
                };
                await createFleetDocumentAPI(formValues.id, "TireSet", formDocument, uploads, t);
                await fetchTireSetDocuments(vehicleDocuments.pagination, formValues.id);
            } catch {
                // error handled by API interceptors
            } finally {
                setIsLoading(false);
            }
        },
        [formValues.id, selectedDocumentTypeId, documentTypes, vehicleDocuments.pagination, t]
    );

    const handleUploadDeleteDialogClicked = useCallback(
        (documentId: string, uploadId: string) => {
            setSelectedFile({ documentId, uploadId });
            setOpenDialog(true);
        },
        [] // React state setteri su stabilni, ne moraš ih navoditi
    );

    const handleDocumentItemDeleteDialogConfirm = useCallback(async () => {
        if (!selectedFile) return;

        try {
            const deleted = await deleteFleetDocumentUploadAPI(
                selectedFile.documentId,
                selectedFile.uploadId,
                t
            );

            if (deleted && formValues.id) {
                await fetchTireSetDocuments(vehicleDocuments.pagination, formValues.id);
            }

            // zatvori dijalog nakon brisanja
            setOpenDialog(false);
            setSelectedFile(initialSelectedFile);
        } catch (err) {
            console.error("Greška prilikom brisanja dokumenta:", err);
        }
    }, [setSelectedFile, t, formValues.id, vehicleDocuments.pagination]);

    const handleDocumentItemDeleteDialogClose = useCallback(() => {
        setSelectedFile(initialSelectedFile);
        setOpenDialog(false);
    }, []);

    const handleDocumentTypeClick = useCallback(
        (documentType: FleetDocumentType) => {
            setSelectedDocumentType(documentType.id);
        },
        [setSelectedDocumentType]
    );

    return (
        <>
            <div className="px-4 mb-4">
                <div className="flex items-center justify-center p-6">
                    <ReactSVG src="/media/images/upload.svg" />
                </div>
                <p className="mb-4 text-sm text-slate-600 ">
                    {t("admin.fleet.tire.set.edit.document.add.description")}
                </p>

                <FileUploadControl
                    uploads={[]}
                    onUploadSave={handleUploadSave}
                    onUploadDelete={() => {}}
                    buttonText="Dodaj dokument"
                    modalTitle="Upload dokumenta"
                />
            </div>
            {vehicleDocuments &&
                documentTypes &&
                documentTypes.map((type) => {
                    const relatedDocs = vehicleDocuments.data.filter(
                        (doc) => doc.documentType.id === type.id
                    );

                    const uploads = relatedDocs.flatMap((doc) => doc.childRows || []);

                    const isDocumentTypeEmpty = uploads.length > 0 ? false : true;
                    return (

                        <div
                            key={type.id}
                            className="px-4 cursor-pointer"
                            onClick={
                                isDocumentTypeEmpty
                                    ? () => handleDocumentTypeClick(type)
                                    : undefined
                            }
                        >
                            <div
                                className={classNames(
                                    "p-3 mb-2 border border-slate-300 ",
                                    isDocumentTypeEmpty
                                        ? "border-dashed bg-slate-50 hover:bg-slate-100"
                                        : " "
                                )}
                            >
                                <h3 className="text-sm font-medium uppercase text-slate-600 mb-3">
                                    {type.name}
                                </h3>

                                {uploads.length > 0 ? (
                                    <HorizontalScroll>
                                        {uploads.map((upload) => (
                                            <div
                                                key={upload.originalFileName}
                                                className="bg-slate-100 border border-slate-300 p-3 w-48 shrink-0 relative group"
                                            >
                                                <div className="overflow-hidden block">
                                                    <PictureIcon url={upload.url} size="small" />
                                                    <span className="text-xs">
                                                        {upload.originalFileName}
                                                    </span>
                                                </div>
                                                <span
                                                    className="absolute top-3 right-3 bg-white/80 border-2 border-red-300 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white z-50 cursor-pointer"
                                                    onClick={() =>
                                                        handleUploadDeleteDialogClicked(
                                                            upload.documentId,
                                                            upload.id
                                                        )
                                                    }
                                                >
                                                    <ReactSVG
                                                        src="/media/icons/close.svg"
                                                        className="w-4 h-4 block rounded-full stroke-red-700 stroke-2"
                                                    />
                                                </span>
                                            </div>
                                        ))}
                                    </HorizontalScroll>
                                ) : (
                                    <p className="text-sm text-slate-500 truncate">
                                        {t("admin.fleet.vehicle.create.document.noresults")}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}

            <Dialog
                open={openDialog}
                title={t("admin.fleet.vehicle.edit.document.dialog.delete.title")}
                message={t("admin.fleet.vehicle.edit.document.dialog.delete.message")}
                confirmText={t(
                    "admin.fleet.vehicle.edit.document.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.fleet.vehicle.edit.document.dialog.delete.canceltext"
                )}
                onConfirm={handleDocumentItemDeleteDialogConfirm}
                onCancel={handleDocumentItemDeleteDialogClose}
            />
        </>
    );
};
export default memo(TireSetDocumentForm);
